import CryptoJS from 'crypto-js';

/**
 * Encryption key - REQUIRED from environment variables
 * Generate a strong random key for production (minimum 32 characters)
 */
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

if (!ENCRYPTION_KEY || ENCRYPTION_KEY.length < 32) {
  throw new Error(
    'REACT_APP_ENCRYPTION_KEY is required and must be at least 32 characters long. ' +
    'Please set it in your .env file.'
  );
}

/**
 * Encrypts a string value
 * @param {string} value - The value to encrypt
 * @returns {string} - Encrypted string
 */
const encrypt = (value) => {
  try {
    if (!value) return value;
    return CryptoJS.AES.encrypt(value, ENCRYPTION_KEY).toString();
  } catch (error) {
    return value; // Return original value if encryption fails
  }
};

/**
 * Decrypts an encrypted string
 * @param {string} encryptedValue - The encrypted value to decrypt
 * @returns {string} - Decrypted string
 */
const decrypt = (encryptedValue) => {
  try {
    if (!encryptedValue) return encryptedValue;
    const bytes = CryptoJS.AES.decrypt(encryptedValue, ENCRYPTION_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    // If decryption fails, bytes.toString returns empty string
    // In that case, try to return the original value (might be unencrypted from before)
    if (!decrypted && encryptedValue) {
      // Check if it looks like encrypted data (base64-like)
      if (encryptedValue.includes('/') || encryptedValue.length > 20) {
        return null; // Likely corrupted encrypted data
      }
      return encryptedValue; // Might be old unencrypted data
    }
    return decrypted;
  } catch (error) {
    // Try to return original value in case it's unencrypted legacy data
    return encryptedValue;
  }
};

/**
 * Secure Storage utility that encrypts data before storing in localStorage
 * and decrypts it when retrieving
 * 
 * NOTE: This file uses localStorage internally as the underlying storage mechanism.
 * This is by design - secureStorage is a wrapper that encrypts data before storing
 * in localStorage. The localStorage usage here is necessary and intentional.
 */
const secureStorage = {
  /**
   * Get an item from secure storage
   * @param {string} key - The key to retrieve
   * @returns {string|null} - The decrypted value or null
   */
  getItem: (key) => {
    try {
      const encryptedValue = localStorage.getItem(key);
      if (encryptedValue === null) return null;
      
      const decrypted = decrypt(encryptedValue);
      return decrypted;
    } catch (error) {
      return null;
    }
  },

  /**
   * Set an item in secure storage (encrypts before storing)
   * @param {string} key - The key to store
   * @param {string} value - The value to encrypt and store
   */
  setItem: (key, value) => {
    try {
      if (value === null || value === undefined) {
        secureStorage.removeItem(key);
        return;
      }
      
      const stringValue = typeof value === 'string' ? value : String(value);
      const encrypted = encrypt(stringValue);
      localStorage.setItem(key, encrypted);
    } catch (error) {
      // Fallback to unencrypted storage if encryption fails
      try {
        localStorage.setItem(key, typeof value === 'string' ? value : String(value));
      } catch (fallbackError) {
        // Fallback storage failed
      }
    }
  },

  /**
   * Remove an item from secure storage
   * @param {string} key - The key to remove
   */
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      // Error removing item
    }
  },

  /**
   * Clear all items from secure storage
   */
  clear: () => {
    try {
      localStorage.clear();
    } catch (error) {
      // Error clearing storage
    }
  },

  /**
   * Get a JSON object from secure storage
   * @param {string} key - The key to retrieve
   * @returns {object|null} - The parsed JSON object or null
   */
  getJSON: (key) => {
    try {
      const value = secureStorage.getItem(key);
      if (!value) return null;
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  },

  /**
   * Set a JSON object in secure storage
   * @param {string} key - The key to store
   * @param {object} value - The object to stringify, encrypt and store
   */
  setJSON: (key, value) => {
    try {
      if (value === null || value === undefined) {
        secureStorage.removeItem(key);
        return;
      }
      const jsonString = JSON.stringify(value);
      secureStorage.setItem(key, jsonString);
    } catch (error) {
      // Error storing JSON
    }
  },
};

export default secureStorage;

